import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Chat as ChatModel, Message } from '../data/mock';
import { wave } from '../data/mock';
import { usePersonLookup } from '../data/people';
import { Icon } from '../components/Icon';
import { Illustration } from '../components/Illustration';
import { Avatar, ExternalLabel, IconButton, NavBar, PersonAvatar, cx } from '../components/ui';
import {
  pop,
  push,
  openDetail,
  sendMessage,
  setChatUi,
  startCall,
  toggleReaction,
  setState,
  useStore,
  type ChatUi,
} from '../state/store';
import { useWide } from '../shell/adaptive';
import { chatTitle } from './ChatList';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🙏', '🎉'];
const EMPTY_UI: ChatUi = {};

export function ChatScreen({ id }: { id: string }) {
  const chat = useStore(
    (s) => s.chats.find((c) => c.id === id) ?? s.guestChats.find((c) => c.id === id),
  );
  const ui = useStore((s) => s.chatUi[id] ?? EMPTY_UI);
  const lookup = usePersonLookup();
  const wide = useWide();
  const scroller = useRef<HTMLDivElement>(null);
  const count = chat?.messages.length ?? 0;

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, id]);

  // Opening a chat marks it read.
  useEffect(() => {
    if (chat?.unread) {
      const t = setTimeout(
        () =>
          setState((s) => ({
            chats: s.chats.map((c) => (c.id === id ? { ...c, unread: 0 } : c)),
          })),
        600,
      );
      return () => clearTimeout(t);
    }
    return undefined;
  }, [chat?.unread, id]);

  if (!chat) return null;
  const others = chat.members.filter((m) => m !== 'me');
  const other = chat.kind === 'direct' ? lookup(others[0] ?? 'me') : undefined;
  const subtitle = chat.typing
    ? 'typing…'
    : other
      ? other.external
        ? `${other.external === 'Guest' ? 'Guest' : other.external}${other.online ? ' · online' : ''}`
        : other.online
          ? 'online'
          : other.lastSeen
            ? `last seen ${other.lastSeen}`
            : (other.title ?? '')
      : others
          .map((m) => lookup(m))
          .map((p) => p.name.split(' ')[0] + (p.external ? ` (${p.external})` : ''))
          .concat('You')
          .join(', ');

  const byId = new Map(chat.messages.map((m) => [m.id, m]));
  const menuMessage = ui.menuFor ? byId.get(ui.menuFor) : undefined;

  return (
    <div className="screen chat-screen">
      <NavBar
        onBack={wide ? undefined : pop}
        leading={
          other ? (
            <PersonAvatar id={other.id} size={36} />
          ) : (
            <Avatar name={chat.name ?? ''} hue={chat.avatar ?? 'blue'} size={36} group />
          )
        }
        title={chatTitle(chat, lookup)}
        subtitle={<span className={cx(chat.typing && 'typing-text')}>{subtitle}</span>}
        onTitleClick={() => (wide ? undefined : push({ name: 'chatInfo', id }))}
        trailing={
          <>
            <IconButton
              icon="video"
              label="Video call"
              onClick={() => startCall(others, true, chat.kind === 'group' ? chat.name : undefined)}
            />
            <IconButton
              icon="phone"
              label="Voice call"
              onClick={() =>
                startCall(others, false, chat.kind === 'group' ? chat.name : undefined)
              }
            />
          </>
        }
      />
      {chat.liveCall && (
        <div className="live-banner">
          <span className="live-dot" />
          <span className="live-banner-text">
            <strong>Group call</strong> ·{' '}
            {chat.liveCall.participants.map((p) => lookup(p).name.split(' ')[0]).join(', ')}
          </span>
          <button
            type="button"
            className="button button-small button-success"
            onClick={() => startCall(chat.liveCall!.participants, true, chat.name)}
          >
            Join
          </button>
        </div>
      )}

      <div className="messages" ref={scroller}>
        <div className="messages-inner">
          <p className="e2e-note">
            <Icon name="lock" size={13} /> Messages and calls are end-to-end encrypted. Only people
            in this chat can read them.
          </p>
          {chat.messages.map((m, i) => {
            const prev = chat.messages[i - 1];
            const next = chat.messages[i + 1];
            const firstOfRun =
              !prev ||
              prev.from !== m.from ||
              !!m.day ||
              prev.kind === 'system' ||
              prev.kind === 'call';
            const lastOfRun =
              !next ||
              next.from !== m.from ||
              !!next.day ||
              next.kind === 'system' ||
              next.kind === 'call';
            return (
              <MessageRow
                key={m.id}
                chat={chat}
                m={m}
                quoted={m.replyTo ? byId.get(m.replyTo) : undefined}
                firstOfRun={firstOfRun}
                lastOfRun={lastOfRun}
                playing={ui.playing === m.id}
              />
            );
          })}
          {chat.typing && (
            <div className="msg-row is-incoming">
              <div className="bubble typing-bubble" aria-label="typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
        </div>
      </div>

      <Composer chat={chat} ui={ui} replyTo={ui.replyTo ? byId.get(ui.replyTo) : undefined} />

      {menuMessage && <MessageMenu chat={chat} m={menuMessage} />}
      {ui.attachOpen && <AttachSheet chatId={chat.id} />}
    </div>
  );
}

function MessageRow({
  chat,
  m,
  quoted,
  firstOfRun,
  lastOfRun,
  playing,
}: {
  chat: ChatModel;
  m: Message;
  quoted?: Message;
  firstOfRun: boolean;
  lastOfRun: boolean;
  playing: boolean;
}) {
  const lookup = usePersonLookup();
  const mine = m.from === 'me';
  const sender = lookup(m.from);
  const group = chat.kind === 'group';
  const day = m.day && <div className="day-separator">{m.day}</div>;

  if (m.kind === 'system' || m.kind === 'call') {
    return (
      <>
        {day}
        <div className={cx('system-message', m.kind === 'call' && 'is-call')}>
          {m.kind === 'call' && <Icon name="video" size={14} />}
          {m.text} · {m.time}
        </div>
      </>
    );
  }

  return (
    <>
      {day}
      <div
        className={cx(
          'msg-row',
          mine ? 'is-outgoing' : 'is-incoming',
          lastOfRun && 'is-last',
          firstOfRun && 'is-first',
          !!m.reactions?.length && 'has-reactions',
        )}
      >
        {group && !mine && (
          <span className="msg-avatar">
            {lastOfRun ? <PersonAvatar id={m.from} size={28} /> : null}
          </span>
        )}
        <div className="msg-stack">
          <button
            type="button"
            className={cx(
              'bubble',
              mine ? 'bubble-out' : 'bubble-in',
              m.kind === 'image' && 'bubble-media',
            )}
            onClick={() => setChatUi(chat.id, { menuFor: m.id })}
            aria-label="Message options"
          >
            {group && !mine && firstOfRun && (
              <span
                className="bubble-sender"
                style={{ color: `var(--color-avatar-${sender.avatar}-foreground)` }}
              >
                {sender.name}
                {sender.external && <ExternalLabel org={sender.external} />}
              </span>
            )}
            {quoted && <Quote m={quoted} />}
            {m.kind === 'image' && m.image && (
              <Illustration kind={m.image} className="bubble-image" />
            )}
            {m.kind === 'document' && m.doc && (
              <span className="doc-attachment">
                <span className={cx('doc-icon', `doc-${m.doc.type}`)}>
                  {m.doc.type.toUpperCase()}
                </span>
                <span className="doc-text">
                  <span className="doc-name">{m.doc.name}</span>
                  <span className="doc-meta">
                    {m.doc.size} · {m.doc.type.toUpperCase()}
                  </span>
                </span>
              </span>
            )}
            {m.kind === 'voice' && m.voice && (
              <VoiceNote
                chatId={chat.id}
                messageId={m.id}
                voice={m.voice}
                playing={playing}
                mine={mine}
              />
            )}
            {m.text && <span className="bubble-text">{m.text}</span>}
            <span className="bubble-meta">
              {m.edited && 'edited · '}
              {m.time}
              {mine && m.status && <Ticks status={m.status} />}
            </span>
          </button>
          {m.reactions && m.reactions.length > 0 && (
            <div className="reactions">
              {m.reactions.map((r) => (
                <button
                  type="button"
                  key={r.emoji}
                  className={cx('reaction', r.by.includes('me') && 'is-mine')}
                  onClick={() => toggleReaction(chat.id, m.id, r.emoji)}
                  aria-label={`${r.emoji} ${r.by.length}`}
                >
                  {r.emoji}
                  {r.by.length > 1 && <span>{r.by.length}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Ticks({ status }: { status: NonNullable<Message['status']> }) {
  if (status === 'sending')
    return <Icon name="expiry" size={13} className="tick" label="Sending" />;
  return (
    <Icon
      name={status === 'sent' ? 'tickSent' : 'tickDelivered'}
      size={16}
      strokeWidth={2}
      className={cx('tick', status === 'read' && 'is-read')}
      label={status}
    />
  );
}

function Quote({ m }: { m: Message }) {
  const lookup = usePersonLookup();
  const name = m.from === 'me' ? 'You' : lookup(m.from).name;
  const text =
    m.kind === 'voice'
      ? `🎤 Voice message (0:${String(m.voice?.seconds ?? 0).padStart(2, '0')})`
      : m.kind === 'image'
        ? `📷 ${m.text ?? 'Photo'}`
        : m.kind === 'document'
          ? `📄 ${m.doc?.name}`
          : m.text;
  return (
    <span className="quote">
      <span className="quote-name">{name}</span>
      <span className="quote-text">{text}</span>
    </span>
  );
}

function VoiceNote({
  chatId,
  messageId,
  voice,
  playing,
  mine,
}: {
  chatId: string;
  messageId: string;
  voice: { seconds: number; waveform: number[] };
  playing: boolean;
  mine: boolean;
}) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!playing) return undefined;
    const started = Date.now() - progress * voice.seconds * 1000;
    const t = setInterval(() => {
      const p = (Date.now() - started) / (voice.seconds * 1000);
      if (p >= 1) {
        setProgress(0);
        setChatUi(chatId, { playing: undefined });
      } else setProgress(p);
    }, 50);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);
  const secs = playing ? Math.floor(progress * voice.seconds) : voice.seconds;
  return (
    <span className="voice-note">
      <span
        role="button"
        tabIndex={0}
        className="voice-play"
        aria-label={playing ? 'Pause' : 'Play voice message'}
        onClick={(e) => {
          e.stopPropagation();
          setChatUi(chatId, { playing: playing ? undefined : messageId });
        }}
      >
        <Icon name={playing ? 'pause' : 'play'} size={18} filled />
      </span>
      <span className="waveform">
        {voice.waveform.map((h, i) => (
          <span
            key={i}
            className={cx('wave-bar', i / voice.waveform.length < progress && 'is-played')}
            style={{ height: `${Math.round(h * 100)}%` }}
          />
        ))}
      </span>
      <span className="voice-time">
        0:{String(secs).padStart(2, '0')}
        {!mine && <span className="voice-dot" />}
      </span>
    </span>
  );
}

function Composer({ chat, ui, replyTo }: { chat: ChatModel; ui: ChatUi; replyTo?: Message }) {
  const draft = useStore((s) => s.drafts[chat.id] ?? '');
  const lookup = usePersonLookup();
  const [elapsed, setElapsed] = useState(0);
  const setDraft = (v: string) => setState((s) => ({ drafts: { ...s.drafts, [chat.id]: v } }));

  useEffect(() => {
    if (!ui.recording) return undefined;
    const start = ui.recordStart ?? Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 250);
    return () => clearInterval(t);
  }, [ui.recording, ui.recordStart]);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    sendMessage(chat.id, { kind: 'text', text, replyTo: ui.replyTo });
    setDraft('');
    setChatUi(chat.id, { replyTo: undefined });
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (ui.recording) {
    const seconds = Math.max(elapsed, 0);
    return (
      <div className="composer is-recording">
        <IconButton
          icon="delete"
          label="Cancel recording"
          className="danger"
          onClick={() => setChatUi(chat.id, { recording: false })}
        />
        <span className="recording-dot" />
        <span className="recording-time">
          {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
        </span>
        <span className="recording-wave">
          {wave(seconds + 5, 28).map((h, i) => (
            <span
              key={i}
              className="wave-bar is-live"
              style={{ height: `${Math.round(h * 100)}%`, animationDelay: `${i * 40}ms` }}
            />
          ))}
        </span>
        <button
          type="button"
          className="send-button"
          aria-label="Send voice message"
          onClick={() => {
            sendMessage(chat.id, {
              kind: 'voice',
              voice: { seconds: Math.max(seconds, 3), waveform: wave(seconds + 17) },
            });
            setChatUi(chat.id, { recording: false });
          }}
        >
          <Icon name="send" size={20} />
        </button>
      </div>
    );
  }

  return (
    <div className="composer-wrap">
      {replyTo && (
        <div className="reply-preview">
          <span className="quote">
            <span className="quote-name">
              Replying to {replyTo.from === 'me' ? 'yourself' : lookup(replyTo.from).name}
            </span>
            <span className="quote-text">
              {replyTo.text ?? (replyTo.kind === 'voice' ? '🎤 Voice message' : '📎 Attachment')}
            </span>
          </span>
          <IconButton
            icon="close"
            label="Cancel reply"
            size={18}
            onClick={() => setChatUi(chat.id, { replyTo: undefined })}
          />
        </div>
      )}
      <div className="composer">
        <IconButton
          icon="add"
          label="Attach"
          onClick={() => setChatUi(chat.id, { attachOpen: true })}
        />
        <label className="composer-field">
          <textarea
            rows={1}
            value={draft}
            placeholder="Message"
            aria-label="Message"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
          />
          <Icon name="emoji" size={21} className="composer-emoji" />
        </label>
        {draft.trim() ? (
          <button type="button" className="send-button" onClick={send} aria-label="Send">
            <Icon name="send" size={20} />
          </button>
        ) : (
          <>
            <IconButton
              icon="camera"
              label="Camera"
              onClick={() => sendMessage(chat.id, { kind: 'image', image: 'team' })}
            />
            <IconButton
              icon="microphone"
              label="Record voice message"
              onClick={() => setChatUi(chat.id, { recording: true, recordStart: Date.now() })}
            />
          </>
        )}
      </div>
    </div>
  );
}

function MessageMenu({ chat, m }: { chat: ChatModel; m: Message }) {
  const close = () => setChatUi(chat.id, { menuFor: undefined });
  const mine = m.from === 'me';
  const editable = mine && m.id.startsWith('local-') && m.kind === 'text';
  const action = (
    label: string,
    icon: Parameters<typeof Icon>[0]['name'],
    fn: () => void,
    danger?: boolean,
  ) => (
    <button type="button" className={cx('menu-item', danger && 'is-danger')} onClick={fn}>
      <span>{label}</span>
      <Icon name={icon} size={19} />
    </button>
  );
  return (
    <div className="menu-layer" onClick={close}>
      <div className={cx('message-menu', mine && 'is-mine')} onClick={(e) => e.stopPropagation()}>
        <div className="reaction-bar" role="toolbar" aria-label="React">
          {QUICK_REACTIONS.map((e) => (
            <button
              type="button"
              key={e}
              className={cx(
                'reaction-choice',
                m.reactions?.some((r) => r.emoji === e && r.by.includes('me')) && 'is-mine',
              )}
              onClick={() => {
                toggleReaction(chat.id, m.id, e);
                close();
              }}
            >
              {e}
            </button>
          ))}
          <button type="button" className="reaction-choice more" aria-label="More reactions">
            <Icon name="add" size={18} />
          </button>
        </div>
        <div className="menu-preview">
          {m.text ??
            (m.kind === 'voice'
              ? '🎤 Voice message'
              : m.kind === 'document'
                ? `📄 ${m.doc?.name}`
                : '📷 Photo')}
        </div>
        <div className="menu-list">
          {action('Reply', 'reply', () =>
            setChatUi(chat.id, { replyTo: m.id, menuFor: undefined }),
          )}
          {action('Forward', 'forward', close)}
          {m.text && action('Copy', 'copy', close)}
          {editable && action('Edit', 'edit', close)}
          {action('Pin', 'pin', close)}
          {action(mine ? 'Delete for everyone' : 'Delete for me', 'delete', close, true)}
        </div>
      </div>
    </div>
  );
}

function AttachSheet({ chatId }: { chatId: string }) {
  const close = () => setChatUi(chatId, { attachOpen: false });
  const item = (
    label: string,
    icon: Parameters<typeof Icon>[0]['name'],
    hue: string,
    fn: () => void,
  ) => (
    <button
      type="button"
      className="attach-item"
      onClick={() => {
        fn();
        close();
      }}
    >
      <span
        className="attach-icon"
        style={{
          background: `var(--color-avatar-${hue}-background)`,
          color: `var(--color-avatar-${hue}-foreground)`,
        }}
      >
        <Icon name={icon} size={24} />
      </span>
      {label}
    </button>
  );
  return (
    <div className="sheet-layer" onClick={close}>
      <div
        className="sheet attach-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Attach"
      >
        <span className="sheet-grabber" />
        <div className="attach-grid">
          {item('Photos', 'photo', 'blue', () =>
            sendMessage(chatId, { kind: 'image', image: 'trucks2', text: 'From today’s run' }),
          )}
          {item('Camera', 'camera', 'coral', () =>
            sendMessage(chatId, { kind: 'image', image: 'team' }),
          )}
          {item('Document', 'document', 'purple', () =>
            sendMessage(chatId, {
              kind: 'document',
              doc: { name: 'Delivery note 4471.pdf', size: '240 KB', type: 'pdf' },
            }),
          )}
          {item('Location', 'location', 'green', () =>
            sendMessage(chatId, { kind: 'text', text: '📍 Hamburg depot, gate 4' }),
          )}
        </div>
        <p className="sheet-note">
          <Icon name="lock" size={13} /> Files are encrypted on this device before upload.
        </p>
      </div>
    </div>
  );
}

export function ChatInfo({ id }: { id: string }) {
  const chat = useStore(
    (s) => s.chats.find((c) => c.id === id) ?? s.guestChats.find((c) => c.id === id),
  );
  const lookup = usePersonLookup();
  if (!chat) return null;
  const others = chat.members.filter((m) => m !== 'me');
  return (
    <div className="screen">
      <NavBar onBack={pop} title={chat.kind === 'group' ? 'Group info' : 'Contact info'} center />
      <div className="screen-scroll">
        <div className="profile-head">
          {chat.kind === 'group' ? (
            <Avatar name={chat.name ?? ''} hue={chat.avatar ?? 'blue'} size={96} group />
          ) : (
            <PersonAvatar id={others[0] ?? 'me'} size={96} />
          )}
          <h2>{chatTitle(chat, lookup)}</h2>
          <p>
            {chat.kind === 'group'
              ? `${chat.members.length} members · ${chat.description ?? ''}`
              : lookup(others[0] ?? 'me').title}
          </p>
        </div>
        {chat.kind === 'group' && (
          <div className="section-body flush">
            {chat.members.map((pid) => {
              const p = lookup(pid);
              return (
                <div className="list-row" key={pid}>
                  <PersonAvatar id={pid} size={40} />
                  <span className="list-row-text">
                    <span className="list-row-title">
                      {pid === 'me' ? 'You' : p.name}{' '}
                      {p.external && <ExternalLabel org={p.external} />}
                    </span>
                    <span className="list-row-subtitle">{p.title}</span>
                  </span>
                  {pid === 'jonas' && <span className="admin-tag">Admin</span>}
                </div>
              );
            })}
          </div>
        )}
        <button
          type="button"
          className="text-link"
          onClick={() => openDetail({ name: 'chat', id }, 'chats')}
        >
          Back to chat
        </button>
      </div>
    </div>
  );
}
